import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box, Typography, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Chip,
  CircularProgress, Alert, InputAdornment, IconButton, Grid,
  FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle,
  DialogContent, DialogActions, Paper, Stack, Avatar, Tooltip,
  Snackbar, Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ClearIcon from '@mui/icons-material/Clear';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PasswordIcon from '@mui/icons-material/Password';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import AuthService from '../Auth/AuthService';
import PageShell from '../sales/common/PageShell';
import StatCard from '../sales/common/StatCard';
import EmptyState from '../sales/common/EmptyState';

// ---------- Helpers ----------

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const formatMobile = (number) => {
  if (!number) return '—';
  const str = String(number);
  if (str.length === 10) return `${str.slice(0, 3)}-${str.slice(3, 6)}-${str.slice(6)}`;
  return str;
};

// ---------- Sub-components ----------

const DialogField = ({ label, value, mono = false, color }) => (
  <Box>
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        color: 'text.secondary',
        fontSize: 10.5,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      fontWeight={mono ? 700 : 500}
      sx={{
        mt: 0.25,
        fontFamily: mono ? 'monospace' : undefined,
        color: color || 'text.primary',
        wordBreak: 'break-word',
        letterSpacing: mono ? 1 : undefined,
      }}
    >
      {value || '—'}
    </Typography>
  </Box>
);

// ---------- Page ----------

const OtpViewPage = () => {
  const [otpRecords, setOtpRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchMobile, setSearchMobile] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedOtp, setSelectedOtp] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isServerSearch, setIsServerSearch] = useState(false);
  const fetchedRef = useRef(false);

  // ---------- Fetch ----------
  const fetchOtpRecords = async () => {
    setLoading(true);
    try {
      const url = AuthService.pathUrl('GET', '/otp');
      const response = await AuthService.getAuth(url);
      setOtpRecords(response || []);
      setIsServerSearch(false);
    } catch (err) {
      console.error('Error fetching OTP records:', err);
      setSnackbar({ open: true, message: 'Failed to fetch OTP records', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchOtpRecords();
  }, []);

  // ---------- Search (server) ----------
  const handleSearch = async () => {
    if (!searchMobile.trim()) {
      setIsServerSearch(false);
      setPage(0);
      return;
    }
    setLoading(true);
    try {
      const url = AuthService.pathUrl(
        'GET',
        `/api/otp/search/mobile/${searchMobile.trim()}`
      );
      const response = await AuthService.getRequest(url);
      setOtpRecords(response || []);
      setIsServerSearch(true);
      setPage(0);
    } catch (err) {
      console.error('Error searching OTP records:', err);
      setSnackbar({ open: true, message: 'Search failed', severity: 'error' });
      setOtpRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleClearSearch = () => {
    setSearchMobile('');
    setStatusFilter('all');
    setPage(0);
    if (isServerSearch) fetchOtpRecords();
  };

  // ---------- Filtering ----------
  const filteredRecords = useMemo(() => {
    return otpRecords.filter((record) => {
      // Status filter (client)
      if (statusFilter !== 'all') {
        const isUsed = statusFilter === 'used';
        if (record.used !== isUsed) return false;
      }
      // If we're in client mode and user typed, apply local substring filter
      if (!isServerSearch && searchMobile.trim()) {
        const s = searchMobile.trim();
        if (!(record.mobile || '').includes(s)) return false;
      }
      return true;
    });
  }, [otpRecords, statusFilter, searchMobile, isServerSearch]);

  useEffect(() => {
    setPage(0);
  }, [statusFilter, searchMobile]);

  // ---------- Stats ----------
  const stats = useMemo(() => {
    const active = otpRecords.filter((r) => !r.used).length;
    const used = otpRecords.filter((r) => r.used).length;
    const withEmail = otpRecords.filter((r) => r.email).length;
    return { total: otpRecords.length, active, used, withEmail };
  }, [otpRecords]);

  // ---------- Pagination ----------
  const paginatedRecords = filteredRecords.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // ---------- Dialog ----------
  const handleViewDetails = (record) => {
    setSelectedOtp(record);
    setViewDialogOpen(true);
  };

  return (
    <PageShell
      icon={<VpnKeyIcon />}
      title="OTP Management"
      subtitle="Audit and search all OTP records issued to users"
      breadcrumbs={[{ label: 'Dashboard', to: '/' }, { label: 'OTP Records' }]}
      actions={
        <Tooltip title="Refresh">
          <IconButton
            onClick={fetchOtpRecords}
            disabled={loading}
            sx={{
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.5)',
              '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      }
    >
      {/* Stats */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          mb: 3,
          gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
        }}
      >
        <StatCard label="Total OTPs" value={stats.total} icon={<VpnKeyIcon />} color="#2563eb" />
        <StatCard label="Active" value={stats.active} icon={<LockOpenIcon />} color="#059669" />
        <StatCard label="Used" value={stats.used} icon={<LockIcon />} color="#dc2626" />
        <StatCard label="With Email" value={stats.withEmail} icon={<EmailIcon />} color="#7c3aed" />
      </Box>

      {/* Filter bar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2.5,
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.5,
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
        <TextField
          size="small"
          placeholder="Search by mobile number..."
          value={searchMobile}
          onChange={(e) => setSearchMobile(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{ flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PhoneIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: searchMobile && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch} edge="end">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Status"
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="used">Used</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="contained"
          onClick={handleSearch}
          disabled={loading || !searchMobile.trim()}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
          sx={{ minWidth: 130 }}
        >
          Search
        </Button>

        {(searchMobile || statusFilter !== 'all') && (
          <Button variant="outlined" onClick={handleClearSearch} startIcon={<ClearIcon />}>
            Clear
          </Button>
        )}
      </Paper>

      {/* Results summary */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Showing <strong>{paginatedRecords.length}</strong> of{' '}
        <strong>{filteredRecords.length}</strong>{' '}
        {filteredRecords.length === 1 ? 'record' : 'records'}
        {isServerSearch && searchMobile && ` (server match: "${searchMobile}")`}
      </Typography>

      {/* Table */}
      {loading && otpRecords.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      ) : filteredRecords.length === 0 ? (
        <Paper elevation={0} sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
          <EmptyState
            icon={<VpnKeyIcon />}
            title={otpRecords.length === 0 ? 'No OTP records yet' : 'No matches'}
            subtitle={
              otpRecords.length === 0
                ? 'OTPs issued to users will appear here.'
                : 'Try a different mobile number or clear the filters.'
            }
            actionLabel={otpRecords.length > 0 ? 'Clear Filters' : undefined}
            onAction={handleClearSearch}
          />
        </Paper>
      ) : (
        <Paper
          elevation={0}
          sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}
        >
          <TableContainer>
            <Table sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow
                  sx={{
                    '& th': {
                      bgcolor: 'grey.50',
                      fontWeight: 700,
                      fontSize: 12,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      color: 'text.secondary',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                    },
                  }}
                >
                  <TableCell>ID</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>OTP Code</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRecords.map((record) => (
                  <TableRow
                    key={record.id}
                    hover
                    sx={{ '&:last-child td': { borderBottom: 0 } }}
                  >
                    <TableCell>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        #{String(record.id).padStart(5, '0')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PhoneIcon fontSize="small" sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">{formatMobile(record.mobile)}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {record.email ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <EmailIcon fontSize="small" sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography
                            variant="body2"
                            sx={{ wordBreak: 'break-all' }}
                          >
                            {record.email}
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={<PasswordIcon sx={{ fontSize: 14 }} />}
                        label={record.otpCode}
                        size="small"
                        sx={{
                          fontFamily: 'monospace',
                          fontWeight: 700,
                          letterSpacing: 1.5,
                          bgcolor: 'grey.100',
                          color: 'text.primary',
                          border: 'none',
                          '& .MuiChip-icon': { color: 'text.secondary' },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <AccessTimeIcon fontSize="small" sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">{formatDate(record.createdAt)}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={record.used ? <BlockIcon /> : <CheckCircleIcon />}
                        label={record.used ? 'Used' : 'Active'}
                        color={record.used ? 'default' : 'success'}
                        size="small"
                        variant={record.used ? 'outlined' : 'filled'}
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleViewDetails(record)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredRecords.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ borderTop: '1px solid', borderColor: 'divider' }}
          />
        </Paper>
      )}

      {/* Details dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2.5, overflow: 'hidden' },
        }}
      >
        {selectedOtp && (
          <>
            {/* Dialog hero */}
            <Box
              sx={{
                p: 3,
                background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 55%, #60a5fa 100%)',
                color: '#fff',
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.18)',
                    width: 52,
                    height: 52,
                    backdropFilter: 'blur(6px)',
                  }}
                >
                  <VpnKeyIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    OTP Details
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.85 }}>
                    Record #{String(selectedOtp.id).padStart(5, '0')}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <DialogContent dividers sx={{ p: 3 }}>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <DialogField label="Mobile Number" value={formatMobile(selectedOtp.mobile)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DialogField label="Email" value={selectedOtp.email} />
                </Grid>

                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      bgcolor: 'grey.50',
                      border: '1px dashed',
                      borderColor: 'primary.main',
                      textAlign: 'center',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        color: 'text.secondary',
                        fontSize: 10.5,
                        mb: 1,
                      }}
                    >
                      OTP Code
                    </Typography>
                    <Typography
                      variant="h4"
                      fontWeight={700}
                      sx={{
                        fontFamily: 'monospace',
                        letterSpacing: 6,
                        color: 'primary.main',
                      }}
                    >
                      {selectedOtp.otpCode}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DialogField label="Created At" value={formatDate(selectedOtp.createdAt)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DialogField
                    label="Status"
                    value={selectedOtp.used ? 'Used' : 'Active'}
                    color={selectedOtp.used ? 'text.secondary' : 'success.main'}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Alert
                    severity={selectedOtp.used ? 'warning' : 'success'}
                    icon={selectedOtp.used ? <BlockIcon /> : <CheckCircleIcon />}
                    sx={{ borderRadius: 2 }}
                  >
                    {selectedOtp.used
                      ? 'This OTP has been used and is no longer valid.'
                      : 'This OTP is still active and can be used.'}
                  </Alert>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
              <Button variant="outlined" onClick={() => setViewDialogOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageShell>
  );
};

export default OtpViewPage;